import React from 'react';

import { NoteView } from '@ndvr/customer-api';

import { ClockIcon } from '@/components/Icons';
import { MinimalTiptapEditorNoToolbar } from '@/components/minimal-tiptap';
import { Card, CardContent } from '@/components/ui/card';
import { convertPlainTextToHtml, formatDateTime } from 'utils';

interface NoteBodyProps {
    note: NoteView;
    personName: string;
    updatedPersonName: string;
    editorContentClassName?: string;
}

/**
 * Chrome-less read-only note body: the note content followed by the Created / Last
 * Modified footer. The single source for this markup — NoteBodyCard wraps it in the
 * note page's Card, the dock's NoteDockItemView renders it bare.
 */
export const NoteBody = ({ note, personName, updatedPersonName, editorContentClassName }: NoteBodyProps) => (
    <>
        <MinimalTiptapEditorNoToolbar
            key={`${note.subject}-${note.updatedAt}`}
            value={convertPlainTextToHtml(note.content)}
            className="w-full border-0 shadow-none"
            output="html"
            editable={false}
            editorClassName="focus:outline-hidden"
            editorContentClassName={editorContentClassName}
        />

        <div className="mt-4 grid grid-cols-1 gap-6 border-t pt-4 md:grid-cols-2">
            <div>
                <div className="text-muted-foreground mb-2 flex items-center text-sm">
                    <ClockIcon className="mr-2 h-4 w-4" />
                    <span>Created</span>
                </div>
                <div className="font-medium">{formatDateTime(note.createdAt)}</div>
                <div className="text-muted-foreground text-sm">by {personName}</div>
            </div>
            <div>
                <div className="text-muted-foreground mb-2 flex items-center text-sm">
                    <ClockIcon className="mr-2 h-4 w-4" />
                    <span>Last Modified</span>
                </div>
                <div className="font-medium">{formatDateTime(note.updatedAt ?? note.createdAt)}</div>
                <div className="text-muted-foreground text-sm">by {updatedPersonName}</div>
            </div>
        </div>
    </>
);

/**
 * Read-only note body: the note content followed by the Created / Last Modified footer.
 */
export const NoteBodyCard = (props: Omit<NoteBodyProps, 'editorContentClassName'>) => (
    <Card className="bg-white">
        <CardContent className="flex flex-col gap-6 p-6">
            <NoteBody {...props} />
        </CardContent>
    </Card>
);
